import { Injectable, inject } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../../../../../../usermgmt/src/lib/shared/environments/environment.dev';
import { PimCacheService } from './pim-cache.service';
import { PimRequestInfo, matchRead, matchResponse, matchWrite } from './pim-cache.policy';

/**
 * ============================================================================
 *  PimCacheInterceptor — where the cache policy meets real traffic
 * ============================================================================
 * Every step of the Export-DWG wizard is destroyed when the user moves off it,
 * so each one re-fetches its rows on every visit: step 7 -> 6 -> 7 with nothing
 * typed is three round trips for two screens of unchanged data. This is the one
 * place that stops that happening.
 *
 * It sits in the HTTP pipeline rather than in each step's service because both
 * of the app's HTTP wrappers — VlHttpService and HttpService — go through the
 * same HttpClient. One interceptor therefore covers every step, every shared
 * child component and the bounding-box saves that fire from deep inside them,
 * with no step code changed and nothing for a future step to remember to do.
 *
 * It decides nothing itself. Which responses may be kept, what makes them
 * stale, and what a given save dirties are all in pim-cache.policy.ts; this
 * file only asks the policy and does as it is told. The one judgement here is
 * the ORDER below, which is what keeps the layer safe by default:
 *
 *   1. Not armed, or not our backend -> pass straight through, change nothing.
 *   2. A known read  -> serve from cache, or fetch once and keep it.
 *   3. Anything else that writes -> invalidate what it dirtied, or if the
 *      policy does not recognise it, empty the whole cache.
 *
 * Step 3 is the important one. An endpoint nobody has written a rule for still
 * cannot leave stale rows on screen, because an unrecognised save throws
 * everything away. Forgetting to add a rule costs an extra network call; it
 * cannot cost correctness.
 */
@Injectable()
export class PimCacheInterceptor implements HttpInterceptor {
  private readonly cache = inject(PimCacheService);

  /** Only the project backend is governed. Auth, assets and third parties are not ours to reason about. */
  private readonly apiBase = environment.urlMapping.EXTRACTION_API;

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Inert unless this session holds the draft's edit lock — see PimCacheService.
    if (!this.cache.isArmed() || !request.url.startsWith(this.apiBase)) {
      return next.handle(request);
    }

    const info: PimRequestInfo = {
      path: this.pathOf(request.url),
      method: request.method.toUpperCase(),
      body: request.body ?? null
    };

    const read = matchRead(info);
    if (read) return this.handleRead(request, next, info, read);

    // Not a read this layer knows about. A GET cannot dirty anything, so it is
    // simply left alone; anything else is a save.
    if (info.method !== 'GET') return this.handleWrite(request, next, info);

    return this.watchResponse(next.handle(request), info);
  }

  /**
   * A save. What it dirties is thrown away TWICE — once as the request goes
   * out, and again when the answer comes back.
   *
   * Once is not enough, and the gap is the reason. A save is only applied on
   * the server somewhere in the middle of its own round trip, so a read that
   * starts after the save was sent but before it landed comes back with the
   * PRE-save rows — and, having been invalidated a moment earlier, finds an
   * empty cache and stores them. The step would then show the old values until
   * something else happened to dirty them. Bounding-box saves make this easy to
   * hit: they fire on their own the instant a rectangle is drawn, while the
   * user carries on working in the same step.
   *
   * Dirtying again on the response drops anything that was cached during that
   * window. The first pass is still worth doing, so a save that never answers
   * cannot leave rows it was about to change sitting in the cache.
   */
  private handleWrite(
    request: HttpRequest<any>,
    next: HttpHandler,
    info: PimRequestInfo
  ): Observable<HttpEvent<any>> {
    const write = matchWrite(info);
    // No rule for it — so nothing can be trusted. See the policy file header.
    const dirty = () => (write ? this.cache.invalidate(write.invalidates(info)) : this.cache.clear());

    dirty();
    return this.watchResponse(next.handle(request), info).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) dirty();
      })
    );
  }

  /**
   * A known read. It is only cached when the policy can name what it depends
   * on: `tags` returning null means "I cannot work out the dependencies of this
   * particular request", and an entry nobody can invalidate is exactly the kind
   * that goes stale, so it is not kept at all.
   */
  private handleRead(
    request: HttpRequest<any>,
    next: HttpHandler,
    info: PimRequestInfo,
    read: ReturnType<typeof matchRead> & {}
  ): Observable<HttpEvent<any>> {
    const tags = read.scope === 'session' ? read.tags?.(info) ?? null : null;
    if (!tags) return this.watchResponse(next.handle(request), info);

    const key = this.keyOf(request, info);
    return this.cache.through(key, tags, () =>
      this.watchResponse(next.handle(request), info).pipe(
        tap((event) => {
          // Row ids and the like are only knowable once the answer is in, and
          // they are what lets a bounding-box save drop precisely the lists
          // holding that row.
          if (event instanceof HttpResponse && read.responseTags) {
            this.cache.tag(key, read.responseTags(event.body));
          }
        })
      )
    );
  }

  /**
   * Watches an answer for news that the SERVER changed something on its own —
   * today, the tick where the upload poll reports extraction finished. Without
   * this the rows written by that background job would sit behind whatever was
   * cached before it ran. See RESPONSE_RULES for why it is watched here.
   */
  private watchResponse(stream$: Observable<HttpEvent<any>>, info: PimRequestInfo): Observable<HttpEvent<any>> {
    const rule = matchResponse(info);
    if (!rule) return stream$;
    return stream$.pipe(
      tap((event) => {
        if (!(event instanceof HttpResponse)) return;
        const dirtied = rule.invalidates(event.body);
        if (dirtied === 'all') this.cache.clear();
        else if (dirtied?.length) this.cache.invalidate(dirtied);
      })
    );
  }

  /** Origin and query string stripped, so the rules match on path alone. */
  private pathOf(url: string): string {
    const noQuery = url.split('?')[0];
    return noQuery.startsWith(this.apiBase) ? noQuery.slice(this.apiBase.length) : noQuery;
  }

  /**
   * What makes two requests "the same request". The body is part of it because
   * most reads here are POSTs whose filters are the only thing distinguishing
   * one step's rows from another's. Two equivalent bodies written in a
   * different key order hash differently and simply miss the cache, which costs
   * a request and never correctness.
   */
  private keyOf(request: HttpRequest<any>, info: PimRequestInfo): string {
    const params = request.params.toString();
    const body = info.body === null ? '' : JSON.stringify(info.body);
    return `${info.method} ${info.path}${params ? `?${params}` : ''} ${body}`;
  }
}
